import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BookOpenText, Languages, Coins, Sparkles } from "lucide-react";

const KEY = "owp_ethos_seen_v1";

// One-time, dismissible welcome that explains the Sourced Companion ethos + how credits
// work. Shown once per browser after the user is authenticated.
export default function EthosOnboarding({ userId }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch (e) { /* ignore */ }
  }, [userId]);

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-lg" data-testid="ethos-onboarding">
        <div className="space-y-6 py-2">
          <div className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-[#10222A] flex items-center justify-center">
              <BookOpenText className="w-6 h-6 text-[#00E5FF]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl text-white">Welcome to OpenWord AI</h2>
            <DialogTitle className="sr-only">Welcome to OpenWord AI</DialogTitle>
            <DialogDescription className="text-sm text-slate-400 leading-relaxed">
              A study companion with one loyalty: the Word itself.
            </DialogDescription>
          </div>

          <div className="space-y-4">
            <div className="flex gap-3">
              <Languages className="w-5 h-5 text-[#00E5FF] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-sm text-slate-400 leading-relaxed">
                <span className="font-medium text-slate-200">Rooted in the original languages.</span> Every
                insight is grounded in the Hebrew, Aramaic and Greek and their Strong's word senses — never in
                one translation's quirks or any denomination's spin. Translations are only the on-screen rendering.
              </p>
            </div>
            <div className="flex gap-3">
              <Sparkles className="w-5 h-5 text-[#00E5FF] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-sm text-slate-400 leading-relaxed">
                <span className="font-medium text-slate-200">Read freely, always.</span> Reading Scripture, taking
                notes, exploring the concordance and your studies are unlimited and never cost anything.
              </p>
            </div>
            <div className="flex gap-3">
              <Coins className="w-5 h-5 text-[#00E5FF] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-sm text-slate-400 leading-relaxed">
                <span className="font-medium text-slate-200">Credits for the deeper tools.</span> Go Deeper
                conversations and the generators (Devotional, Discussion Questions, Sermon Outline) use a few
                study credits each. Your 7-day trial runs completely unmetered.
              </p>
            </div>
          </div>

          <button
            data-testid="ethos-begin"
            onClick={dismiss}
            className="w-full rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] py-3 text-sm font-medium transition-colors active:scale-[0.99]"
          >
            Begin
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
